import type { VideoServerInterface, VSIReturn } from "./video-server-interface";

type MultipartState = {
  uploadId: string;
  s3Key: string;
  partNumber: number;
  parts: { PartNumber: number; ETag: string }[];
  buffer: Blob[];
  bufferSize: number;
};

/**
 * MOM (Momindum) video server implementation.
 *
 * Uses S3 Multipart Upload for all recording modes (upload-while-recording):
 *  - generateNewRecordingName  → POST /initiate  → gets uploadId + s3Key
 *  - sendVideoBlob             → GET  /part/{n}  → presigned URL → PUT directly to S3
 *  - finishUpload              → flushes last part → POST /finish with ETags
 *
 * When both display and camera are selected, two independent multipart upload
 * sessions are created — one for the display stream (display_*.webm, deviceType
 * "desktop") and one for the camera stream (camera_*.webm, deviceType "video").
 * Both are initiated in a single /initiate-session request. After upload the
 * backend transcodes each stream separately and attaches the camera as a
 * side-by-side document on the presentation.
 *
 * S3 requires every part except the last to be >= 5 MiB.
 * We buffer blobs until MIN_PART_BYTES (6 MiB) before flushing.
 */
export default class MomVideoServer implements VideoServerInterface {
  #serverUrl: string | null = null;
  #token: string | null = null;
  #presentationId: string | null = null;
  #desktopFileName: string | null = null;
  #webcamFileName: string | null = null;
  #isInitialized = false;

  // 6 MiB — safely above S3's 5 MiB minimum part size.
  static readonly #MIN_PART_BYTES = 6 * 1024 * 1024;

  // Wall-clock duration (seconds) captured when MediaRecorder.stop() is called.
  // Consumed by the loadedmetadata listener to fix the video element's duration.
  static #pendingDuration: number | null = null;
  static #patchInstalled = false;

  #desktopMultipart: MultipartState | null = null;
  #webcamMultipart: MultipartState | null = null;

  // Tracks all fire-and-forget #flushPart promises so finishUpload can await them.
  #activeFlushes: Set<Promise<void>> = new Set();

  // Set by getUploadUrl, consumed by addXhrAttributes.
  #xhrPendingDeviceType: string | null = null;
  // Blobs captured from the uploadTracks XHR at end-of-recording; flushed as the final S3 part.
  #pendingLocalMedia: Map<string, Blob> = new Map();

  #startTrim: number | null = null;
  #endTrim: number | null = null;

  // Pre-initiated session for dual-stream recordings. The first
  // generateNewRecordingName call kicks off ONE /initiate-session request for
  // both files and stores the Promise here. The second call awaits the same
  // Promise — no race condition regardless of which deviceType fires first.
  #sessionInitPromise: Promise<{
    folder: string;
    desktop: { fileName: string; uploadId: string; s3Key: string };
    webcam: { fileName: string; uploadId: string; s3Key: string };
  }> | null = null;

  /* ---- VideoServerInterface ---- */

  async initialize(
    serverUrl: string,
    _apiKey: string,
    token: string,
    presentationId: string,
  ): Promise<VSIReturn> {
    if (!MomVideoServer.#patchInstalled) {
      MomVideoServer.#patchInstalled = true;
      MomVideoServer.#installPatches();
    }
    this.#serverUrl = serverUrl;
    this.#token = token;
    this.#presentationId = presentationId;

    // Validate the token and presentation exist before allowing recording to start.
    // This catches expired tokens, deleted presentations, and network/CORS issues
    // upfront — rather than failing silently mid-recording when the first S3 part
    // upload is attempted and data has already been lost.
    const validateUrl = `${serverUrl}/1.0/capture/${presentationId}/validate?token=${token}`;

    try {
      const response = await fetch(validateUrl, { method: "GET" });

      if (response.status === 401 || response.status === 403) {
        return {
          successful: false,
          translationNeeded: true,
          errorMessage: "token-expired-error",
        };
      }

      if (response.status === 404) {
        return {
          successful: false,
          translationNeeded: true,
          errorMessage: "presentation-not-found-error",
        };
      }

      if (!response.ok) {
        return {
          successful: false,
          translationNeeded: true,
          errorMessage: "general-error-message",
        };
      }
    } catch (error) {
      // Network failure or CORS block — fetch throws TypeError, not an HTTP status.
      const message = (error as Error).message ?? "";
      const isCors = message.includes("Failed to fetch")
        || message.includes("NetworkError when attempting to fetch resource");

      return {
        successful: false,
        translationNeeded: true,
        errorMessage: isCors ? "cors-error-message" : "general-error-message",
      };
    }

    this.#isInitialized = true;
    return { successful: true, translationNeeded: false, errorMessage: null };
  }

  async generateNewRecordingName(deviceType: string): Promise<string> {
    this.#assertInitialized("generateNewRecordingName");

    // "desktop" always marks the start of a new recording session.
    // Reset any previous session so a re-record after discard starts clean.
    if (deviceType === "desktop") {
      this.#sessionInitPromise = null;
      this.#desktopMultipart = null;
      this.#webcamMultipart = null;
    }

    // Both recorders call initMediasiteFiles concurrently. We fire ONE
    // /initiate-session request and store the Promise on the first call.
    // The second call awaits the same Promise — no race, no duplicate requests.
    if (!this.#sessionInitPromise) {
      const desktopFileName = `display_${crypto.randomUUID()}.webm`;
      const webcamFileName = `camera_${crypto.randomUUID()}.webm`;
      const url = `${this.#serverUrl}/1.0/capture/${this.#presentationId}/initiate-session?token=${this.#token}`;

      this.#sessionInitPromise = fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files: [desktopFileName, webcamFileName] }),
      }).then(async res => {
        if (!res.ok) { throw new Error(`Failed to initiate upload session: ${res.status}`); }
        const data = await res.json() as {
          folder: string;
          files: { fileName: string; uploadId: string; s3Key: string }[];
        };
        return {
          folder: data.folder,
          desktop: data.files.find(f => f.fileName === desktopFileName)!,
          webcam: data.files.find(f => f.fileName === webcamFileName)!,
        };
      });
    }

    const session = await this.#sessionInitPromise;

    if (deviceType === "desktop") {
      this.#desktopFileName = session.desktop.fileName;
      this.#desktopMultipart = {
        uploadId: session.desktop.uploadId,
        s3Key: session.desktop.s3Key,
        partNumber: 1, parts: [], buffer: [], bufferSize: 0,
      };
      return session.desktop.fileName;
    }

    this.#webcamFileName = session.webcam.fileName;
    this.#webcamMultipart = {
      uploadId: session.webcam.uploadId,
      s3Key: session.webcam.s3Key,
      partNumber: 1, parts: [], buffer: [], bufferSize: 0,
    };
    return session.webcam.fileName;
  }

  sendVideoBlob(media: Blob, deviceType: string): Promise<Response> {
    this.#assertInitialized("sendVideoBlob");
    const state = deviceType === "desktop" ? this.#desktopMultipart : this.#webcamMultipart;

    if (!state) {
      return Promise.resolve(new Response(null, { status: 200 }));
    }

    state.buffer.push(media);
    state.bufferSize += media.size;

    // Always return 200 so the recorder advances its uploadIndex and
    // localMedia is empty when uploadTracks runs (its XHR is intercepted below).
    if (state.bufferSize < MomVideoServer.#MIN_PART_BYTES) {
      return Promise.resolve(new Response(null, { status: 200 }));
    }

    // Fire-and-forget: upload in the background, don't block the recorder.
    const p = this.#flushPart(deviceType, state);
    this.#activeFlushes.add(p);
    void p.finally(() => this.#activeFlushes.delete(p));
    return Promise.resolve(new Response(null, { status: 200 }));
  }

  getUploadUrl(deviceType: string): string {
    this.#assertInitialized("getUploadUrl");
    this.#xhrPendingDeviceType = deviceType;
    const fileName = deviceType === "desktop" ? this.#desktopFileName : this.#webcamFileName;
    // Never actually POSTed to — addXhrAttributes intercepts xhr.send.
    return `${this.#serverUrl}/1.0/capture/${this.#presentationId}/${fileName}?token=${this.#token}`;
  }

  addXhrAttributes(xhr: XMLHttpRequest): void {
    const deviceType = this.#xhrPendingDeviceType;
    this.#xhrPendingDeviceType = null;

    const state = deviceType === "desktop" ? this.#desktopMultipart : this.#webcamMultipart;
    if (!state || !deviceType) { return; }

    // The recorder's upload loop may miss the last blob(s) when isStopped fires early.
    // Those blobs land in localMedia and uploadTracks XHRs them here.
    // We intercept xhr.send to:
    //  1. Capture the blob — finishUpload uploads it as the final S3 part.
    //  2. Skip the actual HTTP request — fire a synthetic load event so uploadTracks
    //     resolves normally without hitting uploadVideoAction.
    const pendingLocalMedia = this.#pendingLocalMedia;
    const originalSend = xhr.send.bind(xhr);
    xhr.send = ((body: Document | XMLHttpRequestBodyInit | null) => {
      if (body instanceof Blob && body.size > 0) {
        pendingLocalMedia.set(deviceType, body);
        setTimeout(() => xhr.dispatchEvent(new ProgressEvent("load")), 0);
      } else {
        originalSend(body);
      }
    });
  }

  async finishUpload(): Promise<Response> {
    this.#assertInitialized("finishUpload");

    // Wait for all in-flight background S3 part uploads.
    await Promise.all([...this.#activeFlushes]);

    // Combine remaining buffer + pendingLocalMedia blob into ONE final S3 part per stream.
    // AWS requires every non-final part to be >= 5 MiB; combining avoids that constraint.
    for (const deviceType of ["desktop", "video"]) {
      const state = deviceType === "desktop" ? this.#desktopMultipart : this.#webcamMultipart;
      if (!state) { continue; }

      const pendingBlob = this.#pendingLocalMedia.get(deviceType);
      const finalBlobs: Blob[] = [...state.buffer, ...(pendingBlob ? [pendingBlob] : [])];
      if (finalBlobs.length > 0) {
        const mimeType = finalBlobs[0].type || "video/webm";
        state.buffer = [];
        state.bufferSize = 0;
        await this.#flushPart(deviceType, state, new Blob(finalBlobs, { type: mimeType }));
      }
    }
    this.#pendingLocalMedia.clear();

    // Tell backend to call completeMultipartUpload and set PREVIEW status.
    const files: {
      uploadId: string;
      s3Key: string;
      fileName: string;
      parts: { PartNumber: number; ETag: string }[];
    }[] = [];

    for (const [deviceType, state] of [
      ["desktop", this.#desktopMultipart] as const,
      ["video", this.#webcamMultipart] as const,
    ]) {
      if (state && state.parts.length > 0) {
        const fileName = deviceType === "desktop" ? this.#desktopFileName : this.#webcamFileName;
        files.push({ uploadId: state.uploadId, s3Key: state.s3Key, fileName: fileName!, parts: state.parts });
      }
    }

    const body: Record<string, unknown> = { files };

    if (files.length === 0) {
      throw new Error("finishUpload: no parts were uploaded successfully — cannot complete multipart upload");
    }

    if (this.#startTrim !== null && this.#startTrim > 0) {
      body.trimStart = Math.floor(this.#startTrim * 1000);
    }
    if (this.#endTrim !== null && this.#endTrim > 0) {
      body.trimEnd = Math.ceil(this.#endTrim * 1000);
    }

    const url = `${this.#serverUrl}/1.0/capture/${this.#presentationId}/finish?token=${this.#token}`;
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  changePresentationState(_state: string): Promise<void> {
    return Promise.resolve();
  }

  cleanupOnSuccessfulUpload(): void {
    this.#desktopMultipart = null;
    this.#webcamMultipart = null;
    this.#desktopFileName = null;
    this.#webcamFileName = null;
    this.#activeFlushes.clear();
    this.#pendingLocalMedia.clear();
    this.#startTrim = null;
    this.#endTrim = null;
    this.#sessionInitPromise = null;
  }

  async pauseUpload(_deviceType: string): Promise<void> {
    // No-op — buffered parts catch up after resume.
  }

  async keepUploadAlive(_deviceType: string): Promise<void> {
    // No-op — S3 multipart uploads don't time out between parts.
  }

  resumeUpload(_deviceType: string): void {
    // No-op.
  }

  setTrimPoints(start: number | null, end: number | null): void {
    this.#startTrim = start;
    this.#endTrim = end;
  }

  /* ---- Private helpers ---- */

  async #flushPart(deviceType: string, state: MultipartState, explicitBlob?: Blob): Promise<void> {
    const combined = explicitBlob ?? new Blob(state.buffer, { type: state.buffer[0]?.type ?? "video/webm" });
    const partNumber = state.partNumber;
    const fileName = deviceType === "desktop" ? this.#desktopFileName : this.#webcamFileName;

    if (!explicitBlob) {
      // Clear buffer immediately to prevent double-flush from concurrent calls.
      state.buffer = [];
      state.bufferSize = 0;
    }
    state.partNumber++;

    // Attempt the upload, retrying once on failure.
    // A fresh presigned URL is fetched each attempt — S3 multipart allows
    // re-uploading the same part number; the last successful PUT wins.
    let lastError: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt > 0) {
        await new Promise(r => setTimeout(r, 2000));
        console.warn(`Retrying S3 part ${partNumber} upload (attempt ${attempt + 1})`);
      }

      try {
        const partParams = new URLSearchParams({
          token: this.#token!,
          uploadId: state.uploadId,
          s3Key: state.s3Key,
        });
        const partUrlRes = await fetch(
          `${this.#serverUrl}/1.0/capture/${this.#presentationId}/${fileName}/part/${partNumber}?${partParams}`,
          { method: "GET" },
        );
        if (!partUrlRes.ok) {
          throw new Error(`Failed to get presigned URL for part ${partNumber}: HTTP ${partUrlRes.status}`);
        }

        const { presignedUrl } = await partUrlRes.json() as { presignedUrl: string };
        const s3Res = await fetch(presignedUrl, {
          method: "PUT",
          body: combined,
          headers: { "Content-Type": "video/webm" },
        });
        if (!s3Res.ok) {
          throw new Error(`S3 part ${partNumber} upload failed: HTTP ${s3Res.status}`);
        }

        const etag = s3Res.headers.get("ETag");
        if (!etag) {
          throw new Error(`S3 part ${partNumber} returned no ETag — check S3 CORS ExposeHeaders config`);
        }

        state.parts.push({ PartNumber: partNumber, ETag: etag });
        return;
      } catch (err) {
        lastError = err;
        console.error(`S3 part ${partNumber} upload error (attempt ${attempt + 1}):`, err);
      }
    }

    throw lastError;
  }

  #assertInitialized(method: string): void {
    if (!this.#isInitialized) {
      throw new Error(`MomVideoServer must be initialized before calling ${method}.`);
    }
  }

  /**
   * Patches MediaRecorder.prototype to track wall-clock recording duration, and
   * installs a document-level loadedmetadata listener that overwrites a video
   * element's duration property when fixWebmDuration has underestimated it.
   *
   * fixWebmDuration computes duration from WebM cluster timestamps. When
   * MediaRecorder emits 1-second timeslice blobs, the last cluster's header may
   * not appear in the concatenated blob, causing the reader to miss the final
   * seconds and write a shorter duration into the WebM metadata.
   */
  static #installPatches() {
    // ---------- MediaRecorder prototype patch ----------
    let recStartMs = 0;
    let recPausedMs = 0;
    let recPauseStartMs = 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const proto = MediaRecorder.prototype as any;
    const origStart: (timeslice?: number) => void = proto.start;
    const origPause: () => void = proto.pause;
    const origResume: () => void = proto.resume;
    const origStop: () => void = proto.stop;

    proto.start = function (timeslice?: number) {
      recStartMs = Date.now();
      recPausedMs = 0;
      recPauseStartMs = 0;
      origStart.call(this, timeslice);
    };
    proto.pause = function () {
      recPauseStartMs = Date.now();
      origPause.call(this);
    };
    proto.resume = function () {
      if (recPauseStartMs > 0) {
        recPausedMs += Date.now() - recPauseStartMs;
        recPauseStartMs = 0;
      }
      origResume.call(this);
    };
    proto.stop = function () {
      MomVideoServer.#pendingDuration = (Date.now() - recStartMs - recPausedMs) / 1000;
      origStop.call(this);
    };

    // ---------- loadedmetadata listener ----------
    // When the review step loads the blob-URL video, override its duration
    // property if the WebM metadata value is off by more than half a second.
    document.addEventListener("loadedmetadata", e => {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement)) { return; }
      if (!video.src.startsWith("blob:")) { return; }

      const dur = MomVideoServer.#pendingDuration;
      if (dur === null) { return; }
      MomVideoServer.#pendingDuration = null; // consume once

      if (!isFinite(video.duration) || Math.abs(video.duration - dur) <= 0.5) { return; }

      Object.defineProperty(video, "duration", { get: () => dur, configurable: true });
      video.dispatchEvent(new Event("durationchange"));
    }, { capture: true });
  }
}
